import { useCart } from '@/hooks/useCart';

const WHATSAPP_NUMBER = '5585982245030';

function buildWhatsAppMessage(items: { name: string; quantity: number }[]): string {
  const lines = items.map((item) => `🍞 *${item.quantity}x* ${item.name}`);
  const total = items.reduce((sum, item) => sum + item.quantity, 0);
  const body = `Olá! Gostaria de fazer o seguinte pedido:%0A%0A${lines.join('%0A')}%0A%0A*Total: ${total} ${total === 1 ? 'item' : 'itens'}*`;
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${body}`;
}

export default function CartDrawer() {
  const { items, isOpen, closeCart, removeItem, updateQuantity, clearCart, totalItems } = useCart();

  const whatsappUrl = buildWhatsAppMessage(items);

  const handleSendOrder = () => {
    window.open(whatsappUrl, '_blank');
    clearCart();
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 z-50 bg-background-50/80 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeCart}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 z-50 h-full w-full sm:w-[420px] bg-background-50 border-l border-secondary-500/15 transition-transform duration-400 ease-out flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-5 border-b border-background-200/60 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center">
              <i className="ri-shopping-bag-3-line text-accent-600" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-foreground-900 text-base">Seu Pedido</h3>
              <p className="text-xs text-foreground-500">{totalItems} {totalItems === 1 ? 'item' : 'itens'}</p>
            </div>
          </div>
          <button
            onClick={closeCart}
            className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:text-foreground-800 hover:bg-background-200 transition-colors cursor-pointer"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-10">
              <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mb-4">
                <i className="ri-shopping-bag-3-line text-3xl text-foreground-300" />
              </div>
              <p className="text-foreground-500 text-sm font-medium mb-1">Seu carrinho está vazio</p>
              <p className="text-foreground-400 text-xs">Adicione produtos do cardápio para começar seu pedido.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-3 p-3 rounded-2xl bg-background-100/70 border border-background-200/50"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-semibold text-foreground-800 text-sm truncate">{item.name}</p>
                    <p className="text-xs text-foreground-500 mt-0.5">{item.quantity}x unidade{item.quantity > 1 ? 's' : ''}</p>
                  </div>

                  {/* Quantity controls */}
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      className="w-7 h-7 rounded-full bg-background-200 flex items-center justify-center text-foreground-600 hover:bg-background-300 transition-colors cursor-pointer"
                    >
                      <i className="ri-subtract-line text-xs" />
                    </button>
                    <span className="w-8 text-center font-label font-semibold text-foreground-800 text-sm">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      className="w-7 h-7 rounded-full bg-background-200 flex items-center justify-center text-foreground-600 hover:bg-background-300 transition-colors cursor-pointer"
                    >
                      <i className="ri-add-line text-xs" />
                    </button>
                  </div>

                  {/* Remove */}
                  <button
                    onClick={() => removeItem(item.id)}
                    className="w-7 h-7 rounded-full bg-accent-100/70 flex items-center justify-center text-accent-500 hover:bg-accent-200 hover:text-accent-700 transition-colors cursor-pointer shrink-0"
                  >
                    <i className="ri-delete-bin-line text-xs" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="px-5 py-4 border-t border-background-200/60 shrink-0 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground-600 font-medium">Total de itens</span>
              <span className="font-heading font-bold text-foreground-900 text-lg">{totalItems}</span>
            </div>

            <button
              onClick={handleSendOrder}
              className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-accent-500 text-white font-label font-bold text-sm hover:bg-accent-600 transition-all duration-300 whitespace-nowrap shadow-sm shadow-accent-200/50 cursor-pointer"
            >
              <i className="ri-whatsapp-line text-lg" />
              Enviar Pedido pelo WhatsApp
            </button>

            <button
              onClick={closeCart}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-full border-2 border-accent-300 text-accent-600 font-label font-semibold text-sm hover:bg-accent-50 hover:border-accent-400 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line" />
              Continuar Comprando
            </button>

            <button
              onClick={clearCart}
              className="w-full px-4 py-2 rounded-full text-foreground-500 font-label text-xs hover:text-accent-600 transition-colors cursor-pointer"
            >
              Limpar carrinho
            </button>
          </div>
        )}
      </div>
    </>
  );
}